import React, { useState } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import {
  X, Eye, Users, Radar, Play, Clock, Share2, MessageCircle, Bookmark, Link2,
  UserPlus, Volume2, VolumeX, ThumbsUp, Laugh, Heart, ShieldCheck, Instagram, Facebook, Linkedin, CheckCircle2, Check,
} from "lucide-react";
import { toast } from "sonner";

const PLAT = { Instagram, Facebook, LinkedIn: Linkedin };

export const STATS = [
  { k: "impressions", label: "Total impressions", icon: Eye },
  { k: "unique_impressions", label: "Unique impressions", icon: Users },
  { k: "reach", label: "Reach", icon: Radar },
  { k: "views", label: "Total views", icon: Play, views: true },
  { k: "time_viewed", label: "Time viewed", icon: Clock, text: true },
  { k: "complete_views_pct", label: "Complete views", icon: Eye, suffix: "%" },
  { k: "shares", label: "Total shares", icon: Share2 },
  { k: "comments", label: "Comments", icon: MessageCircle },
  { k: "saves", label: "Saves", icon: Bookmark },
  { k: "link_clicks", label: "Link clicks", icon: Link2 },
  { k: "profile_visits", label: "Profile visits", icon: UserPlus },
  { k: "follows", label: "New follows", icon: UserPlus },
];

export const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);

export function ReactionsRow({ reactions }) {
  if (!reactions) return null;
  return (
    <div className="flex items-center gap-6">
      {[[ThumbsUp, "#3B82F6", reactions.like], [Laugh, "#E5A93D", reactions.haha], [Heart, "#D95C5C", reactions.love]].map(([Ic, c, val], i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 rounded-full" style={{ background: `${c}1a` }}><Ic size={16} color={c} strokeWidth={2} fill={c} /></span>
          <span className="text-lg font-bold text-ink tabular-nums">{fmt(val)}</span>
        </div>
      ))}
    </div>
  );
}

export function StatCard({ s, m }) {
  return (
    <div className="rounded-xl border p-3.5" style={{ borderColor: "#E7E8E4" }} data-testid={`stat-${s.k}`}>
      <div className="flex items-center gap-1.5 label-caps"><s.icon size={13} strokeWidth={1.75} /> {s.label}</div>
      <div className="text-2xl font-extrabold tracking-tighter text-ink mt-1.5">{s.text ? m[s.k] : fmt(m[s.k])}{s.suffix || ""}</div>
      {s.views && (m.sound_on != null || m.sound_off != null) && (
        <div className="flex items-center gap-3 mt-1 text-xs text-ink-2">
          <span className="inline-flex items-center gap-1"><Volume2 size={12} strokeWidth={1.75} /> {fmt(m.sound_on)}</span>
          <span className="inline-flex items-center gap-1"><VolumeX size={12} strokeWidth={1.75} /> {fmt(m.sound_off)}</span>
        </div>
      )}
    </div>
  );
}

export function EngagementHero({ m, trend }) {
  if (m.engagement_rate == null) return null;
  return (
    <div className="rounded-xl p-4" style={{ background: "var(--grad-green-soft)" }}>
      <div className="label-caps" style={{ color: "#5F8213" }}>Engagement rate</div>
      <div className="flex items-end gap-2 mt-1">
        <span className="text-3xl font-extrabold tracking-tighter text-ink">{m.engagement_rate}%</span>
        <span className="text-sm text-ink-2 mb-1">above your recent average</span>
      </div>
      <div className="h-12 mt-1 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
            <defs><linearGradient id="reachFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#93C020" stopOpacity={0.35} /><stop offset="100%" stopColor="#93C020" stopOpacity={0} /></linearGradient></defs>
            <Tooltip cursor={false} contentStyle={{ borderRadius: 10, border: "1px solid #E7E8E4", fontSize: 12 }} formatter={(v) => [fmt(v), "Reach"]} labelFormatter={() => ""} />
            <Area type="monotone" dataKey="v" stroke="#93C020" strokeWidth={2} fill="url(#reachFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function PostAnalytics({ post, onClose }) {
  const m = post.metrics || {};
  const PlatIcon = PLAT[post.platform] || Instagram;
  const cards = STATS.filter((s) => m[s.k] !== null && m[s.k] !== undefined);
  const trend = (post.reach_trend || []).map((v, i) => ({ d: i, v }));
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const link = `${window.location.origin}/share/post/${post.id}`;
    try { await navigator.clipboard.writeText(link); } catch (e) { /* clipboard blocked */ }
    setCopied(true);
    toast.success("Share link copied — send it to a colleague");
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        className="fixed inset-0 z-40" style={{ background: "rgba(21,21,21,.4)" }} />
      <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-3xl bg-white border-l overflow-y-auto" style={{ borderColor: "#E7E8E4" }} data-testid="post-analytics-panel">

        {/* Header */}
        <div className="relative">
          <img src={post.image} alt="" className="w-full h-36 object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(21,21,21,.1), rgba(21,21,21,.7))" }} />
          <div className="absolute right-4 top-4 flex items-center gap-2">
            <button onClick={share} data-testid="share-stats" className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold text-ink bg-white/95 hover:bg-white transition-colors focus-ring">
              {copied ? <><Check size={14} strokeWidth={2.5} color="#5F8213" /> Copied</> : <><Share2 size={14} strokeWidth={2} /> Share stats</>}
            </button>
            <button className="grid place-items-center w-9 h-9 rounded-xl text-white focus-ring" style={{ background: "rgba(0,0,0,.35)" }} onClick={onClose} data-testid="close-analytics"><X size={18} /></button>
          </div>
          <div className="absolute left-6 bottom-4 right-6 text-white">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider">
              <PlatIcon size={14} strokeWidth={1.75} /> {post.platform} · {post.format} <span className="opacity-70">· {post.date}</span>
            </div>
            <h2 className="text-xl font-bold tracking-tight mt-1">{post.title}</h2>
          </div>
        </div>

        <div className="p-5 space-y-3.5">
          {/* Client sharing */}
          <div className="grid grid-cols-2 gap-3">
            {[["Shared with client", post.shared_with_client], ["Opened by client", post.opened_by_client]].map(([l, val]) => (
              <div key={l} className="rounded-xl border p-3.5 flex items-center gap-3" style={{ borderColor: "#E7E8E4" }}>
                <CheckCircle2 size={20} strokeWidth={2} color={val ? "#678A16" : "#9A9C90"} />
                <div><div className="label-caps">{l}</div><div className="text-lg font-bold text-ink">{val ? "Yes" : "Not yet"}</div></div>
              </div>
            ))}
          </div>

          <EngagementHero m={m} trend={trend} />

          {m.reactions && (
            <div className="rounded-xl border p-4" style={{ borderColor: "#E7E8E4" }}>
              <div className="label-caps mb-2.5">Reactions</div>
              <ReactionsRow reactions={m.reactions} />
            </div>
          )}

          {/* Stat grid — 3 columns so everything fits */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {cards.map((s) => <StatCard key={s.k} s={s} m={m} />)}
          </div>

          <div className="flex items-center gap-2 text-xs text-ink-2 pt-1">
            <ShieldCheck size={15} strokeWidth={1.75} color="#678A16" />
            Verified connected-account analytics · last synced {post.last_synced}
          </div>
        </div>
      </motion.div>
    </>
  );
}
